/**
 * Glass configuration interface (local definition to avoid external dependency issues)
 * Made flexible to handle all liquid glass configuration properties
 */
export interface SidebarLiquidConfig {
  /** Habilitar chromatic aberration (Phase 2) - solo para tooltips por defecto */
  enableChromaticAberration?: boolean
  /** Allow any property to avoid external dependency issues */
  [key: string]: any
}

/**
 * Presets optimizados de Liquid Glass para componentes del Sidebar
 *
 * @description
 * Configuraciones pre-optimizadas para diferentes componentes del sidebar:
 * - `indicator`: Para el FluidHoverIndicator (hover states de items)
 * - `tooltip`: Para tooltips expandidos con contenido rico
 * - `dropdown`: Para menús dropdown y submenús
 *
 * Cada preset está calibrado para:
 * - Performance óptimo en sidebar (blur reducido vs showcase)
 * - Tamaños típicos de componentes de sidebar
 * - Visual balance entre refracción y legibilidad
 */

/**
 * Extended config interface for sidebar liquid glass
 */
export interface ExtendedSidebarLiquidConfig extends SidebarLiquidConfig {
  /** Habilitar chromatic aberration (Phase 2) - solo para tooltips por defecto */
  enableChromaticAberration?: boolean
}

export type SidebarLiquidPreset = 'indicator' | 'tooltip' | 'dropdown' | 'sidebar'

export const sidebarLiquidPresets: Record<SidebarLiquidPreset, SidebarLiquidConfig> = {
  /**
   * Preset para FluidHoverIndicator
   *
   * Optimizado para:
   * - Items de sidebar (~48px altura)
   * - Animaciones suaves con spring physics
   * - Performance en hover rápido
   */
  indicator: {
    // Geometry
    width: 240,
    height: 48,
    radius: 12,
    border: 0.07,

    // Visual Effects
    frost: 0.05,
    blur: 8,
    blurBackground: 6,
    displace: 0.3,
    scale: -200,
    alpha: 0.93,
    lightness: 50,

    // Channels
    x: 'R',
    y: 'B',
    blend: 'difference',

    // Chromatic Aberration (subtle para no distraer)
    r: 0,
    g: 10,
    b: 20,

    // Feature flags
    enableChromaticAberration: false // Mantener simple para hover indicator
  },

  /**
   * Preset para SidebarTooltip
   *
   * Optimizado para:
   * - Tooltips expandidos con contenido (~280x320px)
   * - Máximo realismo visual (chromatic aberration habilitado)
   * - Edge glass 3D para profundidad
   */
  tooltip: {
    // Geometry
    width: 280,
    height: 320,
    radius: 16,
    border: 0.07,

    // Visual Effects (calibrado con showcase dock preset)
    frost: 0.05,
    blur: 11,
    blurBackground: 8,
    displace: 0.2,
    scale: -180,
    alpha: 0.93,
    lightness: 50,

    // Channels
    x: 'R',
    y: 'B',
    blend: 'difference',

    // Chromatic Aberration (moderado para realismo sin distracción)
    r: 0,
    g: 10,
    b: 20,

    // Feature flags
    enableChromaticAberration: true // Phase 2: chromatic aberration completo
  },

  /**
   * Preset para Dropdown/SubMenu
   *
   * Optimizado para:
   * - Menús dropdown (~200x180px)
   * - Legibilidad de texto en lista de opciones
   * - Transiciones rápidas de aparición
   */
  dropdown: {
    // Geometry
    width: 200,
    height: 180,
    radius: 14,
    border: 0.07,

    // Visual Effects
    frost: 0.06,
    blur: 9,
    blurBackground: 8,
    displace: 0.35,
    scale: -180,
    alpha: 0.94,
    lightness: 50,

    // Channels
    x: 'R',
    y: 'B',
    blend: 'difference',

    // Chromatic Aberration (moderado)
    r: 0,
    g: 12,
    b: 22,

    // Feature flags
    enableChromaticAberration: false // Priorizar legibilidad sobre efecto
  },

  /**
   * Preset para SidebarNav (contenedor principal)
   *
   * Optimizado para:
   * - Contenedor de sidebar completo (~256px ancho, altura completa viewport)
   * - Efecto sutil que no distrae del contenido
   * - Background transparency para mostrar refracción
   */
  sidebar: {
    // Geometry
    width: 256,
    height: 800,
    radius: 0,
    border: 0.05,

    // Visual Effects
    frost: 0.05,
    blur: 8,
    blurBackground: 8,
    displace: 0.25,
    scale: -180,
    alpha: 0.92,
    lightness: 50,

    // Channels
    x: 'R',
    y: 'B',
    blend: 'difference',

    // Chromatic Aberration (muy sutil)
    r: 0,
    g: 8,
    b: 16,

    // Feature flags
    enableChromaticAberration: false // Efecto muy sutil para el contenedor principal
  }
}

/**
 * Helper para obtener preset con overrides
 *
 * @example
 * ```tsx
 * const config = getLiquidPreset('indicator', { blur: 12 })
 * ```
 */
export function getLiquidPreset(
  preset: SidebarLiquidPreset,
  overrides?: Partial<SidebarLiquidConfig>
): SidebarLiquidConfig {
  return {
    ...sidebarLiquidPresets[preset],
    ...overrides
  }
}
